import { useState, useRef, useEffect } from 'react'
import { Button } from '@/components/ui'
import { Download, ChevronDown, FileSpreadsheet, FileText, FileDown } from 'lucide-react'
import { exportCSV, exportExcel, exportLeadsPDF, exportStatsPDF, exportQuotazioniCSV, exportQuotazioniExcel } from '@/lib/export'
import type { LeadWithValuation } from '@/types'
import type { ZoneQuotazione } from '@/data/zone-quotazioni'

interface ExportDropdownProps {
  leads: LeadWithValuation[]
  activeTab: 'oggi' | 'leads' | 'quotazioni' | 'correzioni' | 'statistiche' | 'conversazioni' | 'impostazioni'
  allLeads: LeadWithValuation[]
  disabled: boolean
  zones?: ZoneQuotazione[]
}

export function ExportDropdown({ leads, activeTab, allLeads, disabled, zones }: ExportDropdownProps) {
  const [isOpen, setIsOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setIsOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const handleExport = (fn: () => void | Promise<void>) => {
    setIsOpen(false)
    fn()
  }

  return (
    <div ref={ref} className="relative">
      <Button
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled}
        className="bg-primary-600 hover:bg-primary-700"
      >
        <Download className="h-4 w-4 mr-2" />
        Esporta
        <ChevronDown className={`h-4 w-4 ml-1 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </Button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-50 py-1">
          <div className="px-3 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wider">
            Lead ({leads.length})
          </div>
          <button
            onClick={() => handleExport(() => exportExcel(leads))}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <FileSpreadsheet className="h-4 w-4 text-green-600" />
            Excel (.xlsx)
          </button>
          <button
            onClick={() => handleExport(() => exportLeadsPDF(leads))}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <FileText className="h-4 w-4 text-red-500" />
            PDF
          </button>
          <button
            onClick={() => handleExport(() => exportCSV(leads))}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <FileDown className="h-4 w-4 text-blue-500" />
            CSV
          </button>

          {activeTab === 'statistiche' && (
            <>
              <div className="border-t border-gray-100 my-1" />
              <div className="px-3 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                Statistiche
              </div>
              <button
                onClick={() => handleExport(() => exportStatsPDF(allLeads))}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <FileText className="h-4 w-4 text-red-500" />
                Report Statistiche PDF
              </button>
            </>
          )}

          {activeTab === 'quotazioni' && zones && zones.length > 0 && (
            <>
              <div className="border-t border-gray-100 my-1" />
              <div className="px-3 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                Quotazioni ({zones.length})
              </div>
              <button
                onClick={() => handleExport(() => exportQuotazioniExcel(zones))}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <FileSpreadsheet className="h-4 w-4 text-green-600" />
                Quotazioni Excel (.xlsx)
              </button>
              <button
                onClick={() => handleExport(() => exportQuotazioniCSV(zones))}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <FileDown className="h-4 w-4 text-blue-500" />
                Quotazioni CSV
              </button>
            </>
          )}
        </div>
      )}
    </div>
  )
}
